import { Alert, Box, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';

import { confirmSecondIdInfo } from '@/api/document';

import { DatePicker } from '@/components/Form/DatePicker';
import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';
import { Form } from '@/components/Layout';
import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectOperationData } from '@/redux/operation/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import { Option } from '@/types/components/common';
import {
  ConfirmSecondIdPayloadFormType,
  DocumentSection,
  DocumentType,
  RequiredDocumentSection,
  SigningFormType,
} from '@/types/document';
import { CountryISO } from '@/types/places';

import { getAPIErrorMessage, getAPIFormError } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import {
  getAllowedIdentityTypes,
  requiresIdentityExpiryDate,
  requiresIdentityIssueDate,
} from '@/utils/configs';

import {
  DrawerContainer,
  HighlightTitle,
  VerificationLabel,
} from './Drawers.styled';

type SecondIdConfirmDrawerProps = {
  section: RequiredDocumentSection;
  idType?: DocumentType | SigningFormType | string;
  uploadedDocumentId?: number | string;
  clientId?: string | number;
  onConfirmed?: () => void;
} & DrawerProps;

export function SecondIdConfirmDrawer({
  section,
  idType,
  uploadedDocumentId,
  clientId,
  onConfirmed,
  onClose,
  title,
  ...props
}: SecondIdConfirmDrawerProps) {
  const { t, country, language } = useTranslate();
  const queryClient = useQueryClient();
  const { idDocumentTypes, dateFormat, features, isoCode } =
    useAppSelector(selectConfigsState);

  const userData = useAppSelector(selectUserData);
  const operationData = useAppSelector(selectOperationData);
  const { documentByDocType, user, requiredDocumentBySection } = clientId
    ? operationData
    : userData;

  const sectionDocument = useMemo(() => {
    const document = documentByDocType[section.type]?.[idType!];
    return idType ? document : undefined;
  }, [idType, documentByDocType, section.type]);

  const allowedIdentityTypes = useMemo(() => {
    const requiredUploadedDocs = Object.keys(
      requiredDocumentBySection[section.type] || {},
    );
    let allowedTypes = getAllowedIdentityTypes(requiredUploadedDocs, features);
    const primaryDocs = Object.keys(
      documentByDocType[DocumentSection.Identity] || {},
    );

    if (section.type === DocumentSection.SecondID) {
      allowedTypes = allowedTypes.filter(
        docType => !primaryDocs.includes(String(docType)),
      );
    }

    return allowedTypes.map(
      docType =>
        ({
          value: docType,
          text: t(`documentType.${docType}`) || docType,
        }) as Option,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idDocumentTypes, features, documentByDocType, section, user]);

  const countryOptions = useMemo(
    () =>
      COUNTRIES_WITH_POPULAR.map(item => ({
        ...item,
        text: country[item.value],
      })),
    [country],
  );

  const methods = useForm<ConfirmSecondIdPayloadFormType>({
    defaultValues: {
      docType: '',
      docNumber: '',
      issueDate: '',
      expiryDate: '',
      issueCountry: '',
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
    watch,
    setValue,
    clearErrors,
    reset: resetForm,
    trigger,
  } = methods;

  useEffect(() => {
    if (sectionDocument) {
      let issueCountry = sectionDocument.issueCountry || '';
      if (sectionDocument.docType === DocumentType.CBBExtract) {
        issueCountry = isoCode;
      }
      resetForm({
        docType: sectionDocument.docType || '',
        docNumber: sectionDocument.docNumber || '',
        issueDate: sectionDocument.issueDate || '',
        expiryDate: sectionDocument.expiryDate || '',
        issueCountry,
      });
      // trigger validation after resetting the form
      void trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionDocument, isoCode]);

  const {
    mutate,
    isPending,
    error,
    reset: resetMut,
  } = useMutationWithUserData({
    mutationFn: (vars: {
      documentId: number | string;
      payload: ConfirmSecondIdPayloadFormType;
    }) => confirmSecondIdInfo(vars.documentId, vars.payload),
    onFinished: async () => {
      if (clientId) {
        await queryClient.invalidateQueries({
          queryKey: ['minor-holder-details-and-required-documents'],
        });
      }
      onConfirmed?.();
    },
  });

  const idDocType = watch('docType') as string;
  const issueCountry = watch('issueCountry') as CountryISO;
  const isIssueDateHidden = !requiresIdentityIssueDate(idDocType, issueCountry);
  const isExpiryDateHidden = !requiresIdentityExpiryDate(
    idDocType,
    issueCountry,
  );
  const apiError = getAPIFormError<ConfirmSecondIdPayloadFormType>(error);

  useEffect(() => {
    if (isIssueDateHidden) {
      setValue('issueDate', '');
      clearErrors('issueDate');
    }

    if (isExpiryDateHidden) {
      setValue('expiryDate', '');
      clearErrors('expiryDate');
    }
  }, [clearErrors, isExpiryDateHidden, isIssueDateHidden, setValue]);

  const onSubmit = handleSubmit(async payload => {
    resetMut();
    const documentId = sectionDocument?.id || uploadedDocumentId;
    if (documentId) {
      mutate({
        documentId,
        payload: {
          docType: payload.docType,
          docNumber: payload.docNumber,
          issueDate: isIssueDateHidden ? '' : payload.issueDate,
          expiryDate: isExpiryDateHidden ? '' : payload.expiryDate,
          issueCountry: payload.issueCountry,
        },
      });
    }
  });

  return (
    <Drawer
      {...props}
      onClose={onClose}
      title={
        title ||
        t(section.displayText || section.type, {
          defaultValue: t('documentSection.Second_ID.title'),
        })
      }
      bgColor={(theme: Theme) => theme.palette.background.paper}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <DrawerContainer>
        <VerificationLabel display="flex" justifyContent="center">
          <Typography variant="h3" textAlign="center" py={2}>
            <Trans
              lang={language}
              i18nKey="verification.documentUploadBox.reviewAndCompleteInfo"
              components={{
                a1: <HighlightTitle />,
              }}
            />
          </Typography>
        </VerificationLabel>
        <Box width="100%" display="flex" justifyContent="center">
          <FormProvider {...methods}>
            <Form
              onSubmit={
                onSubmit as unknown as React.FormEventHandler<HTMLDivElement>
              }
              {...props}
            >
              <Select
                options={allowedIdentityTypes}
                control={control}
                name="docType"
                error={!!visibleErrors.docType}
                helperText={visibleErrors.docType?.message}
                rules={{
                  required: t(
                    'createNewAccount.basicInfo.form.idDocType.error.required',
                  ),
                }}
                fullWidth
                disabled={isPending}
                placeholder={t(
                  'createNewAccount.basicInfo.form.idDocType.placeHolder',
                )}
                label={t('createNewAccount.basicInfo.form.idDocType.label')}
              />
              <TextInput
                name="docNumber"
                control={control}
                error={!!visibleErrors.docNumber || !!apiError.docNumber}
                helperText={
                  visibleErrors.docNumber?.message || apiError.docNumber
                }
                rules={{
                  required: t(
                    'createNewAccount.basicInfo.form.idDocNumber.error.required',
                  ),
                }}
                disabled={isPending}
                fullWidth
                label={t('createNewAccount.basicInfo.form.idDocNumber.label')}
              />
              <Select
                options={countryOptions}
                control={control}
                name="issueCountry"
                error={!!visibleErrors.issueCountry}
                helperText={visibleErrors.issueCountry?.message}
                rules={{
                  required: t(
                    'createNewAccount.basicInfo.form.countryOfIssue.error.required',
                  ),
                }}
                fullWidth
                disabled={isPending}
                placeholder={t(
                  'createNewAccount.basicInfo.form.countryOfIssue.placeHolder',
                )}
                useDrawerOnMobile
                label={t(
                  'createNewAccount.basicInfo.form.countryOfIssue.label',
                )}
                withSearchBar
              />
              {!isIssueDateHidden && (
                <DatePicker
                  name="issueDate"
                  label={t('createNewAccount.basicInfo.form.idIssueDate.label')}
                  format={dateFormat}
                  control={control}
                  error={!!visibleErrors.issueDate}
                  helperText={visibleErrors.issueDate?.message}
                  rules={{
                    required: t(
                      'createNewAccount.basicInfo.form.idIssueDate.error.required',
                    ),
                  }}
                  disabled={isPending}
                />
              )}
              {!isExpiryDateHidden && (
                <DatePicker
                  name="expiryDate"
                  label={t(
                    'createNewAccount.basicInfo.form.idExpiryDate.label',
                  )}
                  format={dateFormat}
                  control={control}
                  error={!!visibleErrors.expiryDate}
                  helperText={visibleErrors.expiryDate?.message}
                  rules={{
                    required: t(
                      'createNewAccount.basicInfo.form.idExpiryDate.error.required',
                    ),
                  }}
                  disabled={isPending}
                />
              )}
              {error && (
                <Alert
                  icon={<Icon name="alert" />}
                  color="error"
                  variant="standard"
                >
                  {getAPIErrorMessage(error)}
                </Alert>
              )}
              <LoadingButton
                type="submit"
                fullWidth
                isLoading={isPending}
                disabled={!isValid || isPending}
                {...getDataTestId('second-id-submit')}
              >
                {t('createNewAccount.basicInfo.form.button.submit')}
              </LoadingButton>
            </Form>
            <FormErrorLogger
              formName="SecondIdConfirmDrawer"
              control={control}
            />
          </FormProvider>
        </Box>
      </DrawerContainer>
    </Drawer>
  );
}

export default SecondIdConfirmDrawer;
